/**
 * Locale-aware formatting for dates, numbers and money, driven by the active
 * UI language. The Intl tags per language live in config/languages.js
 * (`intl`, `numberIntl`, `dateIntl`, `intlFallback`); in short:
 *
 * - Dates are Gregorian in every language: fa uses 'fa-IR-u-ca-gregory'
 *   (Persian digits and month names, 2026/10/11 rather than the Solar Hijri
 *   1405/7/19), ar uses 'ar-u-ca-gregory-nu-latn' (Arabic month names, Latin
 *   digits: 11/10/2026).
 * - Numbers and money: ar uses Latin digits ('ar-u-nu-latn'), fa keeps
 *   Persian digits.
 * - When the browser has no CLDR data for a language (Chrome lacks az, uz,
 *   kk, ky, tk), the language's `intlFallback` is used instead of the
 *   browser's own locale or CLDR root ("2026 M10 11") for numeric output,
 *   and month / weekday names and relative times ("2 saat əvvəl") come from
 *   config/nativeDates.json (CLDR data generated by
 *   scripts/i18n-native-dates.mjs), so an Azerbaijani reader never sees
 *   Turkish "Ekim" / "Pazar" / "önce" and a Kazakh reader never sees Russian
 *   "октября". ky always takes its names from there: its numeric dates use
 *   ru-KG (see config/languages.js), its names are Kyrgyz.
 *
 * Use these instead of hard-coding 'tr-TR' in toLocaleString / Intl calls:
 *
 *   import { formatDate, formatNumber, formatCurrency } from '../utils/format';
 *   formatDate(invoice.date)                     // 04.05.2026 | 5/4/2026 | ...
 *   formatDateTime(row.created_at)
 *   formatNumber(qty, { maximumFractionDigits: 2 })
 *   formatCurrency(total)                        // ₺1.234,50 (default TRY)
 *   formatCurrency(total, 'USD')
 *
 * The language is read at call time; components re-render on a language
 * change because they use useTranslation(). Every helper returns '-' for
 * null / empty / invalid input. An optional last `lang` argument forces a
 * language (e.g. a PDF in the customer's language).
 */
import i18n from '../i18n';
import { activeLanguage, getLanguage } from '../config/languages';
import NATIVE_DATES from '../config/nativeDates.json';

// 11 October 2026: in CLDR root (a locale without data) the long month is "M10".
const PROBE_DATE = Date.UTC(2026, 9, 11, 12);
const dataCache = new Map();

/**
 * True when this runtime has real CLDR formatting data for `tag`. Some
 * browsers report a locale as supported (supportedLocalesOf) but format it
 * with root data, or resolve it to the browser's own locale; both count as
 * "no data".
 */
export function hasLocaleData(tag) {
  if (dataCache.has(tag)) return dataCache.get(tag);
  let ok = false;
  try {
    const dtf = new Intl.DateTimeFormat(tag, { month: 'long', timeZone: 'UTC' });
    const base = (t) => String(t).toLowerCase().split('-')[0];
    ok = base(dtf.resolvedOptions().locale) === base(tag) && !/^M\d+$/.test(dtf.format(PROBE_DATE));
  } catch (_) {
    ok = false;
  }
  dataCache.set(tag, ok);
  return ok;
}

const pick = (tag, entry, probe) => (entry.intlFallback && !probe(tag) ? entry.intlFallback : tag);

/**
 * Intl tags of a language entry after the runtime check:
 * { number, date, speech }. `probe` is injectable for tests.
 */
export function resolveIntlTags(entry, probe = hasLocaleData) {
  return {
    number: pick(entry.numberIntl || entry.intl, entry, probe),
    date: pick(entry.dateIntl || entry.numberIntl || entry.intl, entry, probe),
    speech: entry.intl,
  };
}

const entryFor = (lang) => getLanguage(lang || activeLanguage(i18n));

/** BCP-47 tag for numbers and money, for the given language or the active one. */
export const getLocaleTag = (lang) => resolveIntlTags(entryFor(lang)).number;

/**
 * BCP-47 tag for dates and times: like getLocaleTag, plus the calendar the
 * language is shown in (Gregorian for fa and ar). Use it for any direct
 * Intl.DateTimeFormat / toLocaleDateString call.
 */
export const getDateLocaleTag = (lang) => resolveIntlTags(entryFor(lang)).date;

/** Plain tag for speech recognition / synthesis and og:locale ("ar-SA"). */
export const getSpeechLocaleTag = (lang) => entryFor(lang).intl;

/**
 * Native date data (config/nativeDates.json) for a language whose resolved
 * date tag `tag` cannot name its months and weekdays: the runtime has no
 * CLDR data for it, or the tag belongs to another language (az -> tr-TR,
 * kk -> ru-KZ on Chrome; ky -> ru-KG always). null when Intl can do it.
 */
export function nativeDateData(code, tag, probe = hasLocaleData) {
  const data = NATIVE_DATES.languages[code];
  if (!data) return null;
  const base = String(tag).toLowerCase().split('-')[0];
  return base !== code || !probe(tag) ? data : null;
}

const TIME_FIELDS = ['hour', 'minute', 'second', 'fractionalSecondDigits'];
const TIME_OPTIONS = [...TIME_FIELDS, 'hour12', 'hourCycle', 'timeZoneName', 'dayPeriod'];
const DATE_STYLES = {
  full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
};
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Date with native month / weekday names (see nativeDateData); `timeTag`
 * formats the time part, if any. Returns null when the options ask for no
 * month or weekday name (numeric dates: Intl with the fallback tag is right).
 */
export function formatNativeDate(date, options, data, timeTag) {
  const o = options || {};
  const fields = o.dateStyle ? DATE_STYLES[o.dateStyle] : o;
  if (!fields) return null; // dateStyle: 'short' is numeric
  const { weekday, year, month, day } = fields;
  const textMonth = month === 'long' ? 'long' : (month === 'short' || month === 'narrow') ? 'short' : '';
  if (!textMonth && !weekday) return null;
  const w = weekday ? (weekday === 'long' ? 'long' : 'short') : '';
  const m = textMonth || (month ? 'numeric' : '');
  const pattern = data.patterns[`${w}|${year ? 'y' : ''}|${m}|${day ? 'd' : ''}`];
  if (!pattern) return null;
  // Calendar fields in the requested time zone (Gregorian, Latin digits).
  const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
    timeZone: o.timeZone, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(date);
  const num = (type) => Number(parts.find((p) => p.type === type).value);
  const y = num('year');
  const mo = num('month') - 1;
  const d = num('day');
  const wd = new Date(Date.UTC(y, mo, d)).getUTCDay();
  const width = textMonth || 'long';
  const values = {
    day: day === '2-digit' ? pad2(d) : String(d),
    year: year === '2-digit' ? pad2(y % 100) : String(y),
    monthNum: month === '2-digit' ? pad2(mo + 1) : String(mo + 1),
    month: data.months.format[width][mo],
    Month: data.months.standalone[width][mo],
    weekday: w && data.weekdays.format[w][wd],
    Weekday: w && data.weekdays.standalone[w][wd],
  };
  const text = pattern.replace(/\{(\w+)\}/g, (_, k) => values[k]);
  let timeOptions = null;
  if (o.timeStyle) {
    timeOptions = { timeStyle: o.timeStyle, timeZone: o.timeZone };
  } else if (TIME_FIELDS.some((k) => o[k] !== undefined)) {
    timeOptions = { timeZone: o.timeZone };
    for (const k of TIME_OPTIONS) if (o[k] !== undefined) timeOptions[k] = o[k];
  }
  if (!timeOptions) return text;
  return `${text}${data.dateTimeGlue}${new Intl.DateTimeFormat(timeTag, timeOptions).format(date)}`;
}

/** "2 saat əvvəl" from native relative-time phrases (see nativeDateData). */
export function formatNativeRelative(value, unit, data) {
  const rel = data.relative[unit];
  const n = Object.is(value, -0) ? 0 : value;
  if (rel.auto && rel.auto[String(n)] !== undefined) return rel.auto[String(n)];
  return (n < 0 ? rel.past : rel.future).replace('{0}', String(Math.abs(n)));
}

const nativeFor = (lang) => {
  const entry = entryFor(lang);
  const tag = resolveIntlTags(entry).date;
  return { tag, data: nativeDateData(entry.code, tag) };
};

/**
 * Parse a stored date/time for display on this PC.
 * - Timestamps are stored in UTC; the backend writes most of them without an
 *   offset ("2026-10-13T07:00:00.123456"), which JS would read as local time
 *   (3 hours early in Türkiye). Those are read as UTC.
 * - A bare calendar date ("2026-10-13": invoice, due date) is that day on
 *   this PC, not UTC midnight (which shows the previous day west of UTC).
 * - A time without seconds ("2026-10-21T10:00") is what a datetime-local
 *   input sends (a meeting, an appointment): wall-clock time, shown as is.
 *   Read as UTC it showed 3 hours late (10:00 -> 13:00).
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})$/;
const NAIVE_DATETIME = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(\.\d+)?$/;

export const parseStoredDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  let d;
  if (typeof value === 'string') {
    const s = value.trim();
    const day = DATE_ONLY.exec(s);
    const wall = WALL_CLOCK.exec(s);
    const naive = NAIVE_DATETIME.exec(s);
    if (day) d = new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
    else if (wall) d = new Date(Number(wall[1]), Number(wall[2]) - 1, Number(wall[3]), Number(wall[4]), Number(wall[5]));
    else if (naive) d = new Date(`${naive[1]}T${naive[2]}${(naive[3] || '').slice(0, 4)}Z`);
    else d = new Date(s);
  } else {
    d = new Date(value);
  }
  return isNaN(d.getTime()) ? null : d;
};

const safeDate = parseStoredDate;

/** YYYY-MM-DD of a date in this PC's time zone (not UTC). */
export const toISODate = (value = new Date()) => {
  const d = value instanceof Date ? value : parseStoredDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Today's YYYY-MM-DD on this PC. */
export const todayISO = () => toISODate(new Date());

/** YYYY-MM-DDTHH:mm in this PC's time zone, the value a datetime-local
 * input expects (toISOString().slice(0, 16) gave the UTC clock, SRV-15). */
export const toLocalDateTimeInput = (value = new Date()) => {
  const d = value instanceof Date ? value : parseStoredDate(value);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${toISODate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Local midnight of the Monday on or before `value`. getDate() - getDay() + 1
 * jumped to the next week on a Sunday (getDay() = 0; SRV-13, PRD-11). */
export const startOfWeek = (value = new Date()) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return isNaN(n) ? null : n;
};

/** Short date in the active locale (04.05.2026 in tr, 5/4/2026 in en). */
export const formatDate = (value, options, lang) => {
  const d = safeDate(value);
  if (!d) return '-';
  const { tag, data } = nativeFor(lang);
  const native = data && formatNativeDate(d, options, data, tag);
  return native || d.toLocaleDateString(tag, options);
};

/** Date and time in the active locale. */
export const formatDateTime = (value, options, lang) => {
  const d = safeDate(value);
  if (!d) return '-';
  const { tag, data } = nativeFor(lang);
  const native = data && formatNativeDate(d, options, data, tag);
  return native || d.toLocaleString(tag, options);
};

/** Time of day in the active locale. */
export const formatTime = (value, options, lang) => {
  const d = safeDate(value);
  if (!d) return '-';
  return d.toLocaleTimeString(getDateLocaleTag(lang), options);
};

/** Relative time, e.g. "2 hours ago" in en (Intl.RelativeTimeFormat). */
export const formatRelative = (value, lang) => {
  const d = safeDate(value);
  if (!d) return '-';
  const diffMs = d.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const { tag, data } = nativeFor(lang);
  const rtf = data
    ? { format: (n, unit) => formatNativeRelative(n, unit, data) }
    : new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
  const minutes = 60 * 1000;
  const hours = 60 * minutes;
  const days = 24 * hours;
  if (absMs < hours) return rtf.format(Math.round(diffMs / minutes), 'minute');
  if (absMs < days) return rtf.format(Math.round(diffMs / hours), 'hour');
  if (absMs < 30 * days) return rtf.format(Math.round(diffMs / days), 'day');
  if (absMs < 365 * days) return rtf.format(Math.round(diffMs / (30 * days)), 'month');
  return rtf.format(Math.round(diffMs / (365 * days)), 'year');
};

/** Number with the locale's grouping and decimal separators. */
export const formatNumber = (value, options, lang) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return n.toLocaleString(getLocaleTag(lang), options);
};

/**
 * Money in the active locale. `currency` is an ISO 4217 code (default TRY).
 * An unknown code falls back to "1.234,50 XYZ" instead of throwing.
 */
export const formatCurrency = (value, currency = 'TRY', options, lang) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  const tag = getLocaleTag(lang);
  try {
    return new Intl.NumberFormat(tag, { style: 'currency', currency, ...options }).format(n);
  } catch (_) {
    const number = n.toLocaleString(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${number} ${currency}` : number;
  }
};

/** A percentage the way the locale writes it: 40.7 -> "%40,7" in Turkish. */
export const formatPercent = (value, digits = 1, lang) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return new Intl.NumberFormat(getLocaleTag(lang), {
    style: 'percent', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(n / 100);
};

/** An exchange rate with four decimals in the locale's separators (49,3573). */
export const formatRate = (value, lang) => formatNumber(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 }, lang);
